import { CSSProperties } from 'react';
import { theme } from 'styles/theme/theme';

/**
 * Тон исхода — общий словарь цветных компонентов кита: бейджа статуса (`UDStatusBadge`),
 * фильтр-чипа (`UDFilterChip`) и плитки с иконкой (`UDIconTile`). Какой статус фичи каким тоном рисовать, решает фича.
 */
export type UDStatusTone = 'success' | 'pending' | 'error' | 'neutral';

type ThemeColor = keyof typeof theme.colors;

/** Роли цвета внутри тона: спокойное состояние и выбранное (у интерактивного компонента). */
interface ToneColors {
  surface: ThemeColor;
  ink: ThemeColor;
  line: ThemeColor;
  surfaceSelected: ThemeColor;
  inkSelected: ThemeColor;
  lineSelected: ThemeColor;
  /** Насыщенная заливка тона: плитка `UDIconTile` с белой иконкой. */
  solid: ThemeColor;
}

/**
 * Единственное место, где тон превращается в цвета. Три исхода — состояния темы `Case*`;
 * нейтральный — роли поверхности и чернил, а выбранный — тёмная заливка `InkPrimary` с белым
 * текстом. Не жёлтым акцентом: жёлтый занят тоном `pending`, и выбранный «Все» рядом с
 * невыбранным «Ожидают решения» читался как тот же чип.
 */
const TONE_COLORS: Record<UDStatusTone, ToneColors> = {
  success: {
    surface: 'CaseSuccessSurface',
    ink: 'CaseSuccessText',
    line: 'CaseSuccess',
    surfaceSelected: 'CaseSuccessSurfaceActive',
    inkSelected: 'CaseSuccessText',
    lineSelected: 'CaseSuccessText',
    solid: 'CaseSuccess',
  },
  pending: {
    surface: 'CasePendingSurface',
    ink: 'CasePendingText',
    line: 'CasePending',
    surfaceSelected: 'CasePendingSurfaceActive',
    inkSelected: 'CasePendingText',
    lineSelected: 'CasePendingText',
    solid: 'CasePending',
  },
  error: {
    surface: 'CaseErrorSurface',
    ink: 'CaseErrorText',
    line: 'CaseError',
    surfaceSelected: 'CaseErrorSurfaceActive',
    inkSelected: 'CaseErrorText',
    lineSelected: 'CaseErrorText',
    solid: 'CaseError',
  },
  neutral: {
    surface: 'SurfaceSunken',
    ink: 'InkSecondary',
    line: 'LineSubtle',
    surfaceSelected: 'InkPrimary',
    inkSelected: 'InkOnDark',
    lineSelected: 'InkPrimary',
    // Примитив, а не роль `InkSecondary`: у плитки нейтральный solid светлее (5.6:1 с белой иконкой — для крупной
    // иконки достаточно), а `InkSecondary` слился бы с тёмной заливкой выбранного нейтрального чипа.
    solid: 'Gray',
  },
};

/**
 * CSS-переменные тона для корня компонента (`style`). Компонент красит себя только через них
 * (`bg-(--tone-surface)`, `aria-pressed:bg-(--tone-surface-selected)`), поэтому тон — это
 * набор переменных, а форма и состояния — классы самого компонента.
 *
 * Значения берутся из объекта темы, а не из `--color-*`: Tailwind вырезает переменные цветов,
 * которыми никто не красит классом (`.claude/docs/ui-kit-showcase.md`), и `var()` на такой
 * цвет молча дал бы пустоту.
 */
export function statusToneStyle(tone: UDStatusTone): CSSProperties {
  const colors = TONE_COLORS[tone];

  return {
    '--tone-surface': theme.colors[colors.surface],
    '--tone-ink': theme.colors[colors.ink],
    '--tone-line': theme.colors[colors.line],
    '--tone-surface-selected': theme.colors[colors.surfaceSelected],
    '--tone-ink-selected': theme.colors[colors.inkSelected],
    '--tone-line-selected': theme.colors[colors.lineSelected],
    '--tone-solid': theme.colors[colors.solid],
  } as CSSProperties;
}
